// frontend/src/pages/PicksPage.js
import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import api from '../api'
import { getToken } from '../auth'
import Navbar from '../components/Navbar'
import { useCurrentBasho } from '../hooks/useCurrentBasho'

const h2hCacheMap = new Map()

const MONTH_NAMES_P = { '01': 'January', '03': 'March', '05': 'May', '07': 'July', '09': 'September', '11': 'November' }

function fmtBashoName(id) {
  if (!id) return ''
  const mm = id.slice(4, 6)
  return `${MONTH_NAMES_P[mm] || mm} ${id.slice(0, 4)}`
}

// Returns an array of 1-based ranks with ties (e.g. [1,2,3,3,5,6])
function computeRanks(arr, isTied) {
  const ranks = new Array(arr.length)
  let rank = 1
  for (let i = 0; i < arr.length; i++) {
    if (i > 0 && isTied(arr[i], arr[i - 1])) {
      ranks[i] = ranks[i - 1]
    } else {
      ranks[i] = rank
    }
    rank = i + 2
  }
  return ranks
}

function formatRank(rank) {
  if (!rank) return null
  // "Maegashira 12 East" → "Maegashira 12E"
  const maeg = rank.match(/Maegashira\s+(\d+)\s*(East|West)?/i)
  if (maeg) return `Maegashira ${maeg[1]}${maeg[2] ? maeg[2][0].toUpperCase() : ''}`
  // Named ranks (Yokozuna, Ozeki, Sekiwake, Komusubi) — strip any East/West suffix
  return rank.replace(/\s+(East|West)$/i, '').trim()
}

const font = "'Noto Serif JP', Georgia, serif"
const ink = '#1A0A00'
const muted = '#8A7A5A'
const accent = '#C41E3A'
const cardBorder = '#D4C8A8'

export default function PicksPage() {
  const { basho, day } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const fromLeaderboard = location.state?.fromLeaderboard
  const currentUser = localStorage.getItem('username')
  const { basho: currentBasho, day: currentDay, loading: bashoLoading, status: bashoStatus, daysUntil, startDate } = useCurrentBasho()

  const [matches, setMatches] = useState([])
  const [selections, setSelections] = useState({})
  const [results, setResults] = useState([])
  const [standingsToday, setStandingsToday] = useState([])
  const [standingsBasho, setStandingsBasho] = useState([])
  const [standingsTab, setStandingsTab] = useState('today')
  const [viewingUser, setViewingUser] = useState(null)
  const [viewedPicks, setViewedPicks] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null) // null | 'saving' | 'saved' | 'failed'
  const [submitted, setSubmitted] = useState(false)
  const saveTimerRef = useRef(null)
  const [startTs, setStartTs] = useState(null)
  const [loading, setLoading] = useState(true)
  const [previewMsg, setPreviewMsg] = useState(false)
  const [h2h, setH2h] = useState({})
  const h2hFetchingRef = useRef(false)
  const [allPicksDist, setAllPicksDist] = useState({})
  const [completedBoutsToday, setCompletedBoutsToday] = useState(0)
  const [completedBoutsBasho, setCompletedBoutsBasho] = useState(0)
  const [mobileTab, setMobileTab] = useState('picks')
  const [swipeFeedback, setSwipeFeedback] = useState(null)
  const touchStartRef = useRef(null)
  const swipeTimerRef = useRef(null)
  const [highlightUrl, setHighlightUrl] = useState(null)
  const [adminUnlocked, setAdminUnlocked] = useState(false)

  // Group filter (non-default group users only)
  const userGroupId = localStorage.getItem('groupId')
  const isDefaultGroup = localStorage.getItem('isDefaultGroup') !== 'false'
  const [userGroupName, setUserGroupName] = useState(localStorage.getItem('groupName') || 'My Group')
  const [groupFilter, setGroupFilter] = useState(!isDefaultGroup && userGroupId ? userGroupId : null)

  // Keep group name fresh — admin may rename the group at any time
  useEffect(() => {
    api.get('/user/me')
      .then(res => {
        if (res.data.groupName) {
          setUserGroupName(res.data.groupName)
          localStorage.setItem('groupName', res.data.groupName)
        }
      })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // True when browsing a completed past basho (URL basho differs from live current basho)
  const isPastBasho = !!(basho && currentBasho && basho !== currentBasho)

  useEffect(() => {
    // Always wait for basho detection to complete
    if (bashoLoading) return
    // If no basho in URL (came from /picks route), redirect to current basho
    if (!basho) {
      if (currentBasho) navigate(`/picks/${currentBasho}/${currentDay}`, { replace: true })
      return
    }

    setLoading(true)
    setSelections({})
    setViewingUser(null)
    setViewedPicks({})
    setH2h({})
    setSaveStatus(null)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    const gq = groupFilter ? `&groupId=${groupFilter}` : ''
    Promise.all([
      api.get(`/schedule/${basho}/${day}`),
      api.get(`/picks/${basho}/${day}`),
      api.get(`/results/${basho}/${day}`).catch(() => ({ data: { results: [] } })),
      api.get(`/leaderboard/${basho}/${day}?allUsers=${!isPastBasho}${gq}`).catch(() => ({ data: { leaderboard: [] } })),
      api.get(`/leaderboard/${basho}?throughDay=${day}&allUsers=${!isPastBasho}${gq}`).catch(() => ({ data: { leaderboard: [] } })),
    ])
      .then(([schedRes, picksRes, resultsRes, dailyRes, bashoRes]) => {
        const { matches: m, startDate, adminUnlocked: au } = schedRes.data
        setMatches(m || [])
        setAdminUnlocked(!!au)
        setStartTs(startDate ? Date.parse(startDate) : null)
        const saved = picksRes.data.picks || {}
        const initial = {}
        ;(m || []).forEach((match, i) => {
          const key = `${match.east}|${match.west}`
          if (saved[key]) initial[i] = saved[key]
        })
        setSelections(initial)
        // If every regular bout (non-playoff) already has a saved pick, treat as submitted
        const regularMs = (m || []).filter(mm => !mm.playoff)
        setSubmitted(regularMs.length > 0 && Object.keys(initial).length === regularMs.length)
        setResults(resultsRes.data.results || [])
        setStandingsToday(dailyRes.data.leaderboard || [])
        setStandingsBasho(bashoRes.data.leaderboard || [])
        setCompletedBoutsToday(dailyRes.data.completedBouts || 0)
        setCompletedBoutsBasho(bashoRes.data.completedBouts || 0)

        // Auto-select player from ?player= query param (deep link into past basho)
        const viewAsPlayer = new URLSearchParams(location.search).get('player')
        if (viewAsPlayer) setViewingUser(viewAsPlayer)

        const h2hKey = `${basho}/${day}`
        if (h2hCacheMap.has(h2hKey)) {
          setH2h(h2hCacheMap.get(h2hKey))
        } else {
          api.get(`/h2h/${basho}/${day}`)
            .then(r => {
              const map = {}
              ;(r.data.h2h || []).forEach(item => {
                map[`${item.east}|${item.west}`] = { eastWins: item.eastWins, westWins: item.westWins }
              })
              h2hCacheMap.set(h2hKey, map)
              setH2h(map)
            })
            .catch(() => {})
        }
      })
      .catch(() => alert('Could not load schedule. Try again later.'))
      .finally(() => setLoading(false))
  }, [basho, day, bashoLoading, currentBasho, currentDay, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch standings when group filter changes (skip initial mount — handled above)
  const isFirstGroupMount = useRef(true)
  useEffect(() => {
    if (isFirstGroupMount.current) { isFirstGroupMount.current = false; return }
    if (!basho || !day) return
    const gq = groupFilter ? `?groupId=${groupFilter}` : ''
    const gqa = groupFilter ? `&groupId=${groupFilter}` : ''
    Promise.all([
      api.get(`/leaderboard/${basho}/${day}?allUsers=${!isPastBasho}${gqa}`).catch(() => ({ data: { leaderboard: [], completedBouts: 0 } })),
      api.get(`/leaderboard/${basho}?throughDay=${day}&allUsers=${!isPastBasho}${gqa}`).catch(() => ({ data: { leaderboard: [], completedBouts: 0 } })),
    ]).then(([dailyRes, bashoRes]) => {
      setStandingsToday(dailyRes.data.leaderboard || [])
      setStandingsBasho(bashoRes.data.leaderboard || [])
      setCompletedBoutsToday(dailyRes.data.completedBouts || 0)
      setCompletedBoutsBasho(bashoRes.data.completedBouts || 0)
    }).catch(() => {})
  }, [groupFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewingUser || matches.length === 0) {
      setViewedPicks({})
      return
    }
    api.get(`/picks/${basho}/${day}?user=${viewingUser}`)
      .then(res => {
        const saved = res.data.picks || {}
        const picked = {}
        matches.forEach((m, i) => {
          const key = `${m.east}|${m.west}`
          if (saved[key]) picked[i] = saved[key]
        })
        setViewedPicks(picked)
      })
      .catch(() => setViewedPicks({}))
  }, [viewingUser, basho, day, matches]) // eslint-disable-line react-hooks/exhaustive-deps

  // When viewing a day ahead of the current day, poll the schedule every 5 min
  // so rikishi records update as prior-day results come in via the scheduler.
  useEffect(() => {
    if (!currentDay || parseInt(day, 10) <= currentDay) return
    const id = setInterval(() => {
      api.get(`/schedule/${basho}/${day}`)
        .then(res => { if (res.data.matches?.length) { setMatches(res.data.matches); setAdminUnlocked(!!res.data.adminUnlocked) } })
        .catch(() => {})
    }, 5 * 60 * 1000)
    return () => clearInterval(id)
  }, [basho, day, currentDay]) // eslint-disable-line react-hooks/exhaustive-deps

  // Lock when the first result of the day is recorded — no time-based locking.
  // Also check results.length so that if the page was loaded after bouts started
  // (results in DB but schedule cache still pre-bout), polling still kicks off.
  const isLocked = isPastBasho || (!adminUnlocked && matches.length > 0 && (matches.some(m => m.winner) || results.length > 0))

  // Poll results + standings every 15s while the current day is live (locked = results coming in).
  // Stops once the day is complete (all matches have winners). Never polls for past basho.
  useEffect(() => {
    if (!isLocked || isPastBasho) return
    const allDone = matches.length > 0 && matches.every(m => m.winner)
    if (allDone) return
    const id = setInterval(async () => {
      try {
        // Sync first so DB has latest results before we compute records in the schedule response
        await api.post(`/results/sync/${basho}/${day}`).catch(() => {})
        const schedRes = await api.get(`/schedule/${basho}/${day}`)
        if (schedRes.data.matches?.length) { setMatches(schedRes.data.matches); setAdminUnlocked(!!schedRes.data.adminUnlocked) }
        // Refresh all-picks so auto-filled picks appear without a page reload
        api.get(`/leaderboard/${basho}/${day}/all-picks`)
          .then(res => setAllPicksDist(res.data.picks || {})).catch(() => {})
        const gq = groupFilter ? `?groupId=${groupFilter}` : ''
        const gqa = groupFilter ? `&groupId=${groupFilter}` : ''
        const [dailyRes, bashoRes] = await Promise.all([
          api.get(`/leaderboard/${basho}/${day}?allUsers=${!isPastBasho}${gqa}`),
          api.get(`/leaderboard/${basho}?throughDay=${day}&allUsers=${!isPastBasho}${gqa}`),
        ])
        setStandingsToday(dailyRes.data.leaderboard || [])
        setStandingsBasho(bashoRes.data.leaderboard || [])
        setCompletedBoutsToday(dailyRes.data.completedBouts || 0)
        setCompletedBoutsBasho(bashoRes.data.completedBouts || 0)
      } catch (err) {
        // silently ignore — next tick will retry
      }
    }, 15 * 1000)
    return () => clearInterval(id)
  }, [isLocked, matches, basho, day, groupFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch h2h when matches update and any unfinished bout is missing h2h data.
  // This covers wrestler substitutions: if a wrestler is replaced mid-basho,
  // the old cache key won't match the new wrestler name, so we refresh.
  // Guard: only runs after the initial h2h has been populated (avoids race with the
  // first fetch) and won't fire again while a fetch is already in flight.
  useEffect(() => {
    if (!matches.length || !Object.keys(h2h).length || h2hFetchingRef.current) return
    const unfinished = matches.filter(m => !getWinner(m))
    if (!unfinished.length) return
    const anyMissing = unfinished.some(m => !h2h[`${m.east}|${m.west}`])
    if (!anyMissing) return
    const key = `${basho}/${day}`
    h2hCacheMap.delete(key)
    h2hFetchingRef.current = true
    api.get(`/h2h/${basho}/${day}`)
      .then(r => {
        const map = {}
        ;(r.data.h2h || []).forEach(item => {
          map[`${item.east}|${item.west}`] = { eastWins: item.eastWins, westWins: item.westWins }
        })
        h2hCacheMap.set(key, map)
        setH2h(map)
      })
      .catch(() => {})
      .finally(() => { h2hFetchingRef.current = false })
  }, [matches]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch YouTube highlights video URL for the current day
  useEffect(() => {
    if (!basho || !day) return
    setHighlightUrl(null)
    api.get(`/youtube/highlights/${basho}/${day}`)
      .then(r => { if (r.data.url) setHighlightUrl(r.data.url) })
      .catch(() => {})
  }, [basho, day]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch all-picks for distribution — only after lock, uses existing endpoint
  useEffect(() => {
    if (!isLocked) { setAllPicksDist({}); return }
    api.get(`/leaderboard/${basho}/${day}/all-picks`)
      .then(res => setAllPicksDist(res.data.picks || {}))
      .catch(() => setAllPicksDist({}))
  }, [isLocked, basho, day]) // eslint-disable-line react-hooks/exhaustive-deps

  const getPickDistrib = (m) => {
    if (!isLocked) return null
    const key = `${m.east}|${m.west}`
    let east = 0, west = 0
    for (const userPicks of Object.values(allPicksDist)) {
      const p = userPicks[key]
      if (p === m.east) east++
      else if (p === m.west) west++
    }
    const total = east + west
    if (total === 0) return null
    const ep = Math.round(east / total * 100)
    return { east: ep, west: 100 - ep }
  }

  const getPick = (i) => viewingUser ? viewedPicks[i] : selections[i]
  const regularMatches = matches.filter(m => !m.playoff)
  const regularPickedCount = matches.reduce((n, m, i) => m.playoff ? n : n + (selections[i] !== undefined ? 1 : 0), 0)
  const getResult = (m) => results.find(r => r.east === m.east && r.west === m.west)

  const getWinner = (m) => getResult(m)?.winner || m.winner || null

  // A fusensho (forfeit) bout — winner won without a real match, everyone gets credit
  const isFusen = (m) => getResult(m)?.fusen || m.fusen || false

  const radioState = (wrestler, i, m) => {
    const picked = getPick(i)
    if (picked !== wrestler) return 'empty'
    const winner = getWinner(m)
    if (!winner) return 'selected'
    if (isFusen(m)) return 'correct'   // everyone wins on a fusensho
    return winner === wrestler ? 'correct' : 'wrong'
  }

  // Index of the first match that hasn't finished yet — that's the one on the dohyo
  const firstUnfinishedIdx = matches.findIndex(m => !getWinner(m))

  const resultBoxState = (i, m) => {
    const winner = getWinner(m)
    if (winner) {
      const picked = getPick(i)
      if (!picked) return 'neutral'
      if (isFusen(m)) return 'won'     // everyone gets the win on a fusensho
      return picked === winner ? 'won' : 'lost'
    }
    if (isLocked && i === firstUnfinishedIdx) return 'on-dohyo'
    return 'upcoming'
  }

  const handleSelect = async (i, choice) => {
    console.log(`[handleSelect] i=${i} choice=${choice} isLocked=${isLocked} basho=${basho} day=${day}`)
    if (isLocked || viewingUser) return
    const m = matches[i]
    setSelections(prev => ({ ...prev, [i]: choice }))
    setSubmitted(false)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setSaveStatus('saving')
    try {
      const token = getToken()
      console.log(`[autosave] token=${token ? token.slice(0, 20) + '…' : 'NULL — not logged in'}`)
      console.log(`[autosave] POST /picks/${basho}/${day}`, { east: m.east, west: m.west, picked: choice })
      await api.post(`/picks/${basho}/${day}`, {
        picks: [{ east: m.east, west: m.west, picked: choice }],
      })
      setSaveStatus('saved')
      saveTimerRef.current = setTimeout(() => setSaveStatus(null), 3000)
    } catch (err) {
      const status = err.response?.status
      const msg = err.response?.data?.error || err.message
      console.error(`[autosave] ${status || 'network'}: ${msg}`, { basho, day, east: m.east, west: m.west, picked: choice })
      setSaveStatus(`failed:${status || 'network'}:${msg}`)
    }
  }

  const retryLastSave = async () => {
    setSaveStatus('saving')
    try {
      await api.post(`/picks/${basho}/${day}`, {
        picks: matches
          .map((m, i) => selections[i] ? { east: m.east, west: m.west, picked: selections[i] } : null)
          .filter(Boolean),
      })
      setSaveStatus('saved')
      saveTimerRef.current = setTimeout(() => setSaveStatus(null), 3000)
    } catch (err) {
      const status = err.response?.status
      const msg = err.response?.data?.error || err.message
      console.error(`[retry] ${status || 'network'}: ${msg}`)
      setSaveStatus(`failed:${status || 'network'}:${msg}`)
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await api.post(`/picks/${basho}/${day}`, {
        picks: matches.map((m, i) => ({ east: m.east, west: m.west, picked: selections[i] })),
      })
      setSaveStatus('submitted')
      setSubmitted(true)
      saveTimerRef.current = setTimeout(() => setSaveStatus(null), 4000)
      // Refresh standings so the green dot appears immediately
      const gq = groupFilter ? `?groupId=${groupFilter}` : ''
      const gqa = groupFilter ? `&groupId=${groupFilter}` : ''
      const [dailyRes, bashoRes] = await Promise.all([
        api.get(`/leaderboard/${basho}/${day}?allUsers=${!isPastBasho}${gqa}`).catch(() => ({ data: { leaderboard: [], completedBouts: 0 } })),
        api.get(`/leaderboard/${basho}?throughDay=${day}&allUsers=${!isPastBasho}${gqa}`).catch(() => ({ data: { leaderboard: [], completedBouts: 0 } })),
      ])
      setStandingsToday(dailyRes.data.leaderboard || [])
      setStandingsBasho(bashoRes.data.leaderboard || [])
      setCompletedBoutsToday(dailyRes.data.completedBouts || 0)
      setCompletedBoutsBasho(bashoRes.data.completedBouts || 0)
    } catch (err) {
      const status = err.response?.status
      const msg = err.response?.data?.error || err.message
      setSaveStatus(`failed:${status || 'network'}:${msg}`)
    } finally {
      setSubmitting(false)
    }
  }

  const handleViewUser = (username) => {
    if (!isLocked) {
      setPreviewMsg(true)
      setTimeout(() => setPreviewMsg(false), 2500)
      return
    }
    if (username === currentUser || username === viewingUser) {
      setViewingUser(null)
    } else {
      setViewingUser(username)
    }
  }

  const totalBoutsBasho = parseInt(day, 10) * (matches.length || 1)

  // For the Today tab, calculate standings client-side from live match data + allPicksDist.
  // This means standings update as soon as match winners arrive in the schedule response,
  // without waiting for the DB import cycle.
  const completedBoutsLive = matches.filter(m => !m.playoff && getWinner(m)).length
  const todayStandingsLive = (() => {
    if (completedBoutsLive === 0 || Object.keys(allPicksDist).length === 0) return null
    const rows = standingsToday.map(r => {
      const userPicks = allPicksDist[r.username] || {}
      let wins = 0
      let picksCount = 0
      for (const m of matches) {
        const key = `${m.east}|${m.west}`
        if (userPicks[key]) {
          picksCount++
          if (getWinner(m) && (isFusen(m) || userPicks[key] === getWinner(m))) wins++
        }
      }
      return {
        username: r.username,
        isBot: r.isBot || false,
        wins,
        losses: completedBoutsLive - wins,
        pct: Math.round(wins / completedBoutsLive * 100),
        hasPicks: regularMatches.length > 0 && r.picksCount === regularMatches.length,
        autoSubmitted: r.autoSubmitted || false,
      }
    }).sort((a, b) => b.wins - a.wins || a.username.localeCompare(b.username))
    return rows
  })()

  const standingsData = standingsTab === 'today'
    ? (todayStandingsLive || standingsToday.map(r => ({
        username: r.username,
        isBot: r.isBot || false,
        wins: null,
        losses: null,
        pct: null,
        hasPicks: regularMatches.length > 0 && r.picksCount === regularMatches.length,
        autoSubmitted: r.autoSubmitted || false,
      })))
    : standingsBasho.map(r => ({
        username: r.username,
        isBot: r.isBot || false,
        wins: completedBoutsBasho > 0 ? r.total : null,
        losses: completedBoutsBasho > 0 && r.total !== null ? Math.max(0, completedBoutsBasho - r.total) : null,
        pct: completedBoutsBasho > 0 && r.total !== null ? Math.round(r.total / completedBoutsBasho * 100) : null,
        hasPicks: r.total !== null,
        autoSubmitted: r.autoSubmitted || false,
      }))

  const handleTouchStart = (e) => {
    const t = e.touches[0]
    touchStartRef.current = { x: t.clientX, y: t.clientY }
  }

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return
    const t = e.changedTouches[0]
    const dx = t.clientX - touchStartRef.current.x
    const dy = t.clientY - touchStartRef.current.y
    touchStartRef.current = null
    if (Math.abs(dx) < 50 || Math.abs(dy) > 75) return
    const dayNum = parseInt(day, 10)
    let newDay
    if (dx > 0) {
      if (dayNum <= 1) return
      newDay = dayNum - 1
    } else {
      if (dayNum >= 15) return
      newDay = dayNum + 1
    }
    if (swipeTimerRef.current) clearTimeout(swipeTimerRef.current)
    setSwipeFeedback({ day: newDay, dir: dx > 0 ? 'prev' : 'next' })
    swipeTimerRef.current = setTimeout(() => setSwipeFeedback(null), 600)
    navigate(`/picks/${basho}/${newDay}`)
  }

  // No schedule data — show appropriate card once loading is done.
  // Data-driven: if the API returned matches the day is available regardless of the day number.
  if (!fromLeaderboard && !bashoLoading && !loading && matches.length === 0) {
    const isUpcoming = bashoStatus === 'upcoming'
    const bashoName = currentBasho ? fmtBashoName(currentBasho) : 'The next basho'
    const formattedStart = startDate
      ? new Date(startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' })
      : null

    return (
      <div style={{ minHeight: '100vh', background: '#F5F0E8', fontFamily: font }}>
        <Navbar currentPage="picks" />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 56px)', padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ background: '#fff', border: `1px solid ${cardBorder}`, borderRadius: 4, padding: '48px 40px', maxWidth: 420, width: '100%' }}>
            {isUpcoming ? (
              <>
                <div style={{ fontSize: 32, marginBottom: 16 }}>⛩️</div>
                <div style={{ fontSize: 13, letterSpacing: 4, color: muted, textTransform: 'uppercase', fontFamily: 'Georgia, serif', marginBottom: 8 }}>Coming soon</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: ink, marginBottom: 12 }}>{bashoName}</div>
                {daysUntil != null && (
                  <div style={{ fontSize: 13, color: '#5C3A1E', fontFamily: 'Georgia, serif', fontStyle: 'italic', marginBottom: 4 }}>
                    Begins in <span style={{ color: accent, fontWeight: 700, fontStyle: 'normal' }}>{daysUntil}</span> {daysUntil === 1 ? 'day' : 'days'}
                    {formattedStart && <span style={{ color: muted }}> · {formattedStart}</span>}
                  </div>
                )}
                <div style={{ width: 60, height: 2, background: ink, margin: '24px auto 24px' }} />
                <div style={{ fontSize: 12, color: '#5C3A1E', fontFamily: 'Georgia, serif', lineHeight: 1.8, fontStyle: 'italic' }}>
                  Picks open on Day 1. Check back then to choose your winners for each bout on the card.
                </div>
                <div style={{ fontSize: 11, color: muted, fontFamily: 'Georgia, serif', lineHeight: 1.7, marginTop: 12 }}>
                  If the schedule has been announced and you're not seeing it yet, our data provider may still be updating. Try again shortly.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 32, marginBottom: 16 }}>🗓️</div>
                <div style={{ fontSize: 13, letterSpacing: 4, color: muted, textTransform: 'uppercase', fontFamily: 'Georgia, serif', marginBottom: 8 }}>Not yet available</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: ink, marginBottom: 12 }}>Day {day}</div>
                <div style={{ width: 60, height: 2, background: ink, margin: '24px auto' }} />
                <div style={{ fontSize: 12, color: '#5C3A1E', fontFamily: 'Georgia, serif', lineHeight: 1.8, fontStyle: 'italic' }}>
                  The Day {day} card hasn't been announced yet.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
    <style>{`
      @keyframes swipeFade { 0% { opacity: 0.9; } 100% { opacity: 0; } }
      .swipe-feedback {
        position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
        background: rgba(26,10,0,0.75); color: #F5F0E8;
        padding: 10px 22px; border-radius: 24px;
        font-size: 13px; font-family: 'Noto Serif JP', Georgia, serif; letter-spacing: 1px;
        pointer-events: none; z-index: 20; white-space: nowrap;
        animation: swipeFade 0.6s ease-out forwards;
      }
      .picks-scroll-area { scrollbar-width: thin; scrollbar-color: #D4C8A8 transparent; }
      .picks-scroll-area::-webkit-scrollbar { width: 4px; }
      .picks-scroll-area::-webkit-scrollbar-thumb { background: #D4C8A8; border-radius: 2px; }
      .picks-tab-bar { display: none; }
      @media (max-width: 768px) {
        .picks-page { height: auto !important; overflow: visible !important; }
        .picks-layout { overflow: visible !important; flex-direction: column !important; padding: 12px 12px 20px !important; }
        .picks-main { overflow: visible !important; min-height: unset !important; }
        .picks-scroll-area { flex: none !important; overflow-y: visible !important; min-height: unset !important; }
        .picks-sidebar { width: 100% !important; }
        .picks-main--hidden { display: none !important; }
        .picks-sidebar--hidden { display: none !important; }
        .picks-tab-bar {
          display: flex; margin-bottom: 12px;
          border: 1px solid #D4C8A8; border-radius: 3px; overflow: hidden;
        }
        .picks-tab-btn {
          flex: 1; padding: 9px 0; background: transparent;
          border: none; cursor: pointer; font-size: 11px; letter-spacing: 2px;
          text-transform: uppercase; font-family: 'Noto Serif JP', Georgia, serif;
          color: #8A7A5A; transition: background 0.1s, color 0.1s;
        }
        .picks-tab-btn + .picks-tab-btn { border-left: 1px solid #D4C8A8; }
        .picks-tab-btn.picks-tab-active { background: #1A0A00; color: #F5F0E8; }
        .picks-bout-row { padding: 8px 10px !important; }
        .picks-wrestler-name { font-size: 12px !important; }
        .picks-wrestler-meta, .picks-wrestler-record { font-size: 9px !important; }
        .picks-center-col { width: 68px !important; }
        .h2h-line { font-size: 9px !important; }
        .picks-progress-wrap {
          position: sticky; top: 56px; z-index: 10;
          background: #F5F0E8; margin: 0 -12px;
          padding: 8px 12px 0; border-bottom: 1px solid #E8E0D0;
        }
      }
    `}</style>
    <div className="picks-page" style={s.page}>
      <Navbar
        currentPage="picks"
        backLink={fromLeaderboard ? { label: '← Leaderboard', to: location.state?.returnTo || `/leaderboard/${basho}/${day}` } : undefined}
      />

      {loading ? (
        <div style={s.loadingState}>Loading…</div>
      ) : (
        <div className="picks-layout" style={s.layout}>

          {/* Mobile tab switcher */}
          <div className="picks-tab-bar">
            <button className={`picks-tab-btn${mobileTab === 'picks' ? ' picks-tab-active' : ''}`} onClick={() => setMobileTab('picks')}>Picks</button>
            <button className={`picks-tab-btn${mobileTab === 'standings' ? ' picks-tab-active' : ''}`} onClick={() => setMobileTab('standings')}>Standings</button>
          </div>

          {/* LEFT: bout list */}
          <main className={`picks-main${mobileTab !== 'picks' ? ' picks-main--hidden' : ''}`} style={s.main}>

            {/* Section header */}
            <div style={s.sectionHeader}>
              <div>
                <div style={s.redLine} />
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <h1 style={s.dayTitle}>Day {day}</h1>
                  {highlightUrl && (
                    <a
                      href={highlightUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={s.ytLink}
                      title={`Watch Day ${day} highlights`}
                    >
                      ▶ Highlights
                    </a>
                  )}
                </div>
                <p style={s.bashoLabel}>{fmtBashoName(basho)}</p>
              </div>
              {!isPastBasho && (
                <div style={s.statusPill(isLocked)}>
                  {isLocked ? 'Locked' : 'Open'}
                </div>
              )}
            </div>

            {/* Viewing note */}
            {viewingUser && (
              <div style={s.viewingNote}>
                Viewing <em>{viewingUser}</em>'s picks ·{' '}
                <span style={s.backMineLink} onClick={() => setViewingUser(null)}>
                  Back to mine
                </span>
              </div>
            )}

            {/* Progress bar + save status + submit (own picks only) */}
            {!viewingUser && !isLocked && regularMatches.length > 0 && (
              <div className="picks-progress-wrap">
                <div style={s.progressRow}>
                  <div style={s.progressTrack}>
                    <div style={{ ...s.progressFill, width: `${(regularPickedCount / regularMatches.length) * 100}%` }} />
                  </div>
                  <span style={s.progressLabel}>{regularPickedCount}/{regularMatches.length}</span>
                  {saveStatus === 'saving' && <span style={s.saveStatusSaving}>Saving…</span>}
                  {saveStatus === 'saved' && <span style={s.saveStatusSaved}>Saved ✓</span>}
                  {saveStatus === 'submitted' && <span style={s.saveStatusSaved}>Submitted ✓</span>}
                  {saveStatus && saveStatus.startsWith('failed') && (
                    <span style={s.saveStatusFailed} onClick={retryLastSave} title={saveStatus}>
                      {saveStatus.includes('403') ? 'Locked' : saveStatus.includes('401') ? 'Not logged in' : saveStatus.includes('network') ? 'Network error — tap to retry' : 'Save failed — tap to retry'}
                    </span>
                  )}
                </div>
                <div style={s.submitArea}>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || regularPickedCount !== regularMatches.length || submitted}
                    style={s.submitBtn(regularPickedCount === regularMatches.length && !submitting && !submitted, submitted && regularPickedCount === regularMatches.length)}
                  >
                    {submitting
                      ? 'Saving…'
                      : submitted && regularPickedCount === regularMatches.length
                        ? 'Submitted ✓'
                        : regularPickedCount === regularMatches.length
                          ? 'Submit Picks →'
                          : `${regularMatches.length - regularPickedCount} more to pick`}
                  </button>
                </div>
              </div>
            )}

            {/* Scrollable bout list */}
            <div
              className="picks-scroll-area"
              style={{ ...s.scrollArea, position: 'relative' }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {swipeFeedback && (
                <div key={swipeFeedback.day} className="swipe-feedback">
                  {swipeFeedback.dir === 'prev' ? `← Day ${swipeFeedback.day}` : `Day ${swipeFeedback.day} →`}
                </div>
              )}
              {matches.length === 0 ? (
                <p style={s.empty}>
                  {startTs && startTs > Date.now()
                    ? `Schedule not yet announced — check back closer to ${new Date(startTs).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.`
                    : `No matches found for Day ${day}.`}
                </p>
              ) : (
                <div style={s.matchList}>
                  {matches.map((m, i) => {
                    const rState = resultBoxState(i, m)
                    const winner = getWinner(m)
                    return (
                      <div key={i} className="picks-bout-row" style={m.playoff ? { ...s.boutRow, background: '#FEF9E8', borderColor: '#D4C880' } : s.boutRow}>
                        {/* East — radio | [name 🔥 / rank / record] */}
                        <div
                          style={{ ...s.side, ...s.eastSide, cursor: isLocked || viewingUser || m.playoff ? 'default' : 'pointer' }}
                          onClick={() => !m.playoff && handleSelect(i, m.east)}
                        >
                          {m.playoff
                            ? <span style={s.playoffBadge}>Playoff</span>
                            : <div style={s.radioCircle(radioState(m.east, i, m))} />}
                          <div style={s.wrestlerInfo}>
                            <div style={s.nameRow}>
                              <span className="picks-wrestler-name" style={s.wrestlerName}>{m.east}</span>
                              {m.eastStreak >= 3 && <span style={s.streakFire}>🔥</span>}
                              {m.eastLossStreak >= 3 && <span style={s.streakFire}>💩</span>}
                            </div>
                            {m.eastRank && <span className="picks-wrestler-meta" style={s.wrestlerMeta}>{formatRank(m.eastRank)}</span>}
                            {m.eastRecord && (
                              <div style={s.recordRow}>
                                <span className="picks-wrestler-record" style={s.wrestlerRecord}>{m.eastRecord}</span>
                                {isLocked && (() => {
                                  const d = getPickDistrib(m)
                                  if (!d) return null
                                  return <>
                                    <span style={s.distribDot}> · </span>
                                    <span style={s.distribPct}>{d.east}%</span>
                                  </>
                                })()}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Center — unchanged */}
                        <div className="picks-center-col" style={s.centerCol}>
                          <span style={s.vsLabel}>VS</span>
                          {(() => {
                            if (winner) return null
                            const rec = h2h[`${m.east}|${m.west}`]
                            if (!rec) return null
                            return (
                              <div style={s.h2hLine} className="h2h-line">
                                <span style={s.h2hEast}>{rec.eastWins}</span>
                                <span style={s.h2hVs}>—</span>
                                <span style={s.h2hWest}>{rec.westWins}</span>
                              </div>
                            )
                          })()}
                          <div style={s.resultBox(rState)}>
                            {rState === 'upcoming'
                              ? '—'
                              : rState === 'on-dohyo'
                                ? 'On dohyo'
                                : isFusen(m)
                                  ? 'Fusen'
                                  : winner || '—'}
                          </div>
                        </div>

                        {/* West — radio | [🔥 name / rank / record right-aligned] mirrored via row-reverse */}
                        <div
                          style={{ ...s.side, ...s.westSide, cursor: isLocked || viewingUser || m.playoff ? 'default' : 'pointer' }}
                          onClick={() => !m.playoff && handleSelect(i, m.west)}
                        >
                          {m.playoff
                            ? <span style={s.playoffBadge}>Playoff</span>
                            : <div style={s.radioCircle(radioState(m.west, i, m))} />}
                          <div style={{ ...s.wrestlerInfo, alignItems: 'flex-end' }}>
                            <div style={s.nameRow}>
                              {m.westLossStreak >= 3 && <span style={s.streakFire}>💩</span>}
                              {m.westStreak >= 3 && <span style={s.streakFire}>🔥</span>}
                              <span className="picks-wrestler-name" style={s.wrestlerName}>{m.west}</span>
                            </div>
                            {m.westRank && <span className="picks-wrestler-meta" style={s.wrestlerMeta}>{formatRank(m.westRank)}</span>}
                            {m.westRecord && (
                              <div style={s.recordRow}>
                                {isLocked && (() => {
                                  const d = getPickDistrib(m)
                                  if (!d) return null
                                  return <>
                                    <span style={s.distribPct}>{d.west}%</span>
                                    <span style={s.distribDot}> · </span>
                                  </>
                                })()}
                                <span className="picks-wrestler-record" style={s.wrestlerRecord}>{m.westRecord}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {isLocked && !isPastBasho && (
              <p style={s.lockedNote}>
                Picks are closed.{' '}
                <a href={`/leaderboard/${basho}/${day}`} style={{ color: accent }}>View results →</a>
              </p>
            )}
          </main>

          {/* RIGHT: standings sidebar */}
          <aside className={`picks-sidebar${mobileTab !== 'standings' ? ' picks-sidebar--hidden' : ''}`} style={s.sidebar}>
            <div style={s.sidebarCard}>
              {/* Group filter (non-default group members only) */}
              {!isDefaultGroup && userGroupId && (
                <div style={s.groupRow}>
                  {[
                    { value: null, label: 'All' },
                    { value: userGroupId, label: userGroupName },
                  ].map(opt => (
                    <button
                      key={opt.value || 'all'}
                      onClick={() => setGroupFilter(opt.value)}
                      style={s.groupPill(groupFilter === opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Radio toggle */}
              <div style={s.toggleRow}>
                {[['today', 'Today'], ['basho', 'Basho']].map(([val, label]) => (
                  <div key={val} style={s.toggleOption} onClick={() => setStandingsTab(val)}>
                    <div style={s.toggleRadio(standingsTab === val)} />
                    <span style={s.toggleText}>{label}</span>
                  </div>
                ))}
              </div>

              {/* Standings header */}
              <div style={s.standingsHeaderRow}>
                <span style={s.standingsTitle}>
                  {standingsTab === 'today' ? `Standings · Day ${day}` : 'Standings · Basho'}
                </span>
                <div style={s.colHeaders}>
                  <span style={s.colH}>W</span>
                  <span style={s.colH}>L</span>
                  <span style={s.colH}>%</span>
                </div>
              </div>
              <div style={s.standingsDivider} />

              {previewMsg && (
                <p style={s.previewMsg}>Picks hidden until lock</p>
              )}

              {/* Player rows */}
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, scrollbarGutter: 'stable' }}>
                {standingsData.length === 0 ? (
                  <p style={s.noData}>No data yet.</p>
                ) : (() => {
                  const sidebarRanks = computeRanks(standingsData, (a, b) => a.wins === b.wins && a.pct === b.pct)
                  return standingsData.map((row, i) => (
                    <div
                      key={row.username}
                      style={s.playerRow(row.username === viewingUser, row.username === currentUser, isLocked)}
                      onClick={() => handleViewUser(row.username)}
                    >
                      <span style={s.playerRank}>{sidebarRanks[i]}</span>
                      {standingsTab === 'today' && !isPastBasho && (
                        <div style={{ ...s.playerDot, background: row.hasPicks ? (row.autoSubmitted ? '#C4A010' : '#1A6A1A') : '#C8BFA8' }} />
                      )}
                      <span style={{ ...s.playerName(row.username === currentUser), display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{row.username}</span>
                        {row.isBot && <span style={s.botBadge}>🤖</span>}
                      </span>
                      <span style={s.statCell}>{row.wins !== null ? row.wins : '—'}</span>
                      <span style={s.statCell}>{row.losses !== null ? row.losses : '—'}</span>
                      <span style={s.statCell}>{row.pct !== null ? `${row.pct}%` : '—'}</span>
                    </div>
                  ))
                })()}
              </div>
            </div>
          </aside>

        </div>
      )}

    </div>
    </>
  )
}

const s = {
  page: {
    height: '100vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: '#F5F0E8',
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(180,160,120,0.06) 3px, rgba(180,160,120,0.06) 4px)',
    color: ink,
  },
  loadingState: { textAlign: 'center', padding: 80, color: muted, fontFamily: font },
  layout: {
    display: 'flex', gap: 20, padding: '20px 20px 16px',
    maxWidth: 1100, margin: '0 auto', width: '100%',
    boxSizing: 'border-box', alignItems: 'stretch',
    flex: 1, overflow: 'hidden', minHeight: 0,
  },
  main: {
    flex: 1, minWidth: 300,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', minHeight: 0,
  },
  scrollArea: {
    flex: 1, overflowY: 'auto', minHeight: 0,
  },

  // Section header
  sectionHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    marginBottom: 20,
  },
  redLine: { width: 28, height: 3, background: accent, marginBottom: 8 },
  dayTitle: { fontSize: 34, fontWeight: 900, color: ink, margin: 0, lineHeight: 1, fontFamily: font },
  ytLink: {
    fontSize: 11, color: accent, fontFamily: font, letterSpacing: 0.5,
    textDecoration: 'none', whiteSpace: 'nowrap', lineHeight: 1,
  },
  bashoLabel: {
    fontSize: 10, color: muted, letterSpacing: 2,
    textTransform: 'uppercase', margin: '4px 0 0', fontFamily: font,
  },
  statusPill: (locked) => ({
    padding: '5px 12px', borderRadius: 20, fontSize: 11, fontFamily: font,
    background: locked ? '#FEE8E8' : '#E8F5E8',
    color: locked ? '#8A1A1A' : '#1A6A1A',
    border: `1px solid ${locked ? '#D4A8A8' : '#A8D4A8'}`,
    whiteSpace: 'nowrap', marginTop: 6,
  }),

  // Viewing note
  viewingNote: {
    fontSize: 13, fontStyle: 'italic', color: '#8A6A1A', fontFamily: font,
    margin: '0 0 16px', padding: '8px 12px',
    background: '#FEF9E8', borderRadius: 4, border: '1px solid #D4C880',
  },
  backMineLink: {
    cursor: 'pointer', textDecoration: 'underline',
    color: '#8A6A1A', fontStyle: 'normal',
  },

  // Progress
  progressRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  progressTrack: { flex: 1, height: 3, background: cardBorder, borderRadius: 2, overflow: 'hidden' },
  progressFill: { height: '100%', background: accent, borderRadius: 2, transition: 'width 0.3s' },
  progressLabel: { fontSize: 11, color: muted, fontFamily: font, whiteSpace: 'nowrap' },

  // Bout list
  matchList: { display: 'flex', flexDirection: 'column', gap: 5 },
  empty: { textAlign: 'center', padding: 60, color: muted, fontFamily: font, fontSize: 14 },

  // Bout row
  boutRow: {
    display: 'grid', gridTemplateColumns: '1fr 82px 1fr',
    background: '#FFFFFF', border: `1px solid ${cardBorder}`,
    borderRadius: 4, padding: '10px 14px', gap: 8, alignItems: 'center',
  },
  side: { display: 'flex', alignItems: 'center', gap: 8 },
  eastSide: { flexDirection: 'row' },
  westSide: { flexDirection: 'row-reverse' },

  playoffBadge: {
    fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase',
    color: '#8A6A1A', fontFamily: "'Noto Serif JP', Georgia, serif",
    fontStyle: 'italic', flexShrink: 0, whiteSpace: 'nowrap',
  },
  radioCircle: (state) => ({
    width: 14, height: 14, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
    ...(state === 'empty'
      ? { border: `2px solid ${cardBorder}`, background: 'transparent' }
      : state === 'selected'
        ? { background: ink, border: `2px solid ${ink}`, boxShadow: 'inset 0 0 0 3px white' }
        : state === 'correct'
          ? { background: '#1A6A1A', border: '2px solid #1A6A1A', boxShadow: 'inset 0 0 0 3px white' }
          : { background: accent, border: `2px solid ${accent}`, boxShadow: 'inset 0 0 0 3px white' }),
  }),

  wrestlerInfo: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
  nameRow: { display: 'flex', alignItems: 'center', gap: 4 },
  wrestlerName: {
    fontSize: 13, fontWeight: 700, color: ink, fontFamily: font,
    lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  streakFire: { fontSize: 12, flexShrink: 0, lineHeight: 1 },
  wrestlerMeta: { fontSize: 10, color: muted, fontFamily: 'Georgia, serif' },
  wrestlerRecord: { fontSize: 10, color: '#5C3A1E', fontFamily: 'Georgia, serif' },

  // Center column
  centerCol: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 4, width: 82,
  },
  vsLabel: {
    fontSize: 9, color: muted, letterSpacing: 1,
    textTransform: 'uppercase', fontFamily: font,
  },
  h2hLine: {
    display: 'flex', alignItems: 'center', width: '100%',
    fontSize: 9, color: muted, fontFamily: 'Georgia, serif',
    lineHeight: 1,
  },
  h2hEast: { flex: 1, textAlign: 'left' },
  h2hVs: { flex: 1, textAlign: 'center', fontSize: 8 },
  h2hWest: { flex: 1, textAlign: 'right' },
  recordRow: { display: 'flex', alignItems: 'center' },
  distribDot: { color: '#D4C8A8', fontSize: 10, fontFamily: 'Georgia, serif', margin: '0 2px' },
  distribPct: { color: '#B4A880', fontSize: 10, fontFamily: 'Georgia, serif' },
  resultBox: (state) => ({
    padding: '3px 6px', borderRadius: 2, fontSize: 10,
    textAlign: 'center', fontFamily: font,
    maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    ...(state === 'upcoming'
      ? { visibility: 'hidden', border: '1px solid transparent', background: 'transparent', color: 'transparent' }
      : state === 'won'
        ? { background: '#E8F5E8', border: '1px solid #A8D4A8', color: '#1A6A1A' }
        : state === 'lost'
          ? { background: '#FEE8E8', border: '1px solid #D4A8A8', color: '#8A1A1A' }
          : state === 'on-dohyo'
            ? { background: '#FEF9E8', border: '1px solid #D4C880', color: '#8A6A1A', fontStyle: 'italic' }
            : { background: '#F5F0E8', border: `1px solid ${cardBorder}`, color: muted }),
  }),

  // Save status
  saveStatusSaving: {
    fontSize: 10, color: muted, fontFamily: 'Georgia, serif', fontStyle: 'italic', whiteSpace: 'nowrap',
  },
  saveStatusSaved: {
    fontSize: 10, color: '#1A6A1A', fontFamily: 'Georgia, serif', whiteSpace: 'nowrap',
  },
  saveStatusFailed: {
    fontSize: 10, color: accent, fontFamily: 'Georgia, serif', cursor: 'pointer',
    textDecoration: 'underline', whiteSpace: 'nowrap',
  },

  // Submit
  submitArea: { marginBottom: 12, textAlign: 'center' },
  submitBtn: (ready, isSubmitted) => ({
    padding: '13px 34px', fontFamily: font, fontSize: 11,
    letterSpacing: 3, textTransform: 'uppercase',
    cursor: ready ? 'pointer' : 'default',
    background: ready ? accent : isSubmitted ? '#E8F5E8' : cardBorder,
    color: ready ? '#F5F0E8' : isSubmitted ? '#1A6A1A' : muted,
    border: isSubmitted ? '1px solid #A8D4A8' : 'none',
    borderRadius: 2, width: '100%', maxWidth: 320,
  }),
  lockedNote: { marginTop: 22, textAlign: 'center', color: muted, fontFamily: font, fontSize: 13 },

  // Sidebar
  sidebar: { width: 370, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 100px)' },
  sidebarCard: {
    background: '#FFFFFF', border: `1px solid ${cardBorder}`,
    borderRadius: 4, padding: '14px',
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0,
  },

  // Toggle
  groupRow: { display: 'flex', gap: 6, marginBottom: 10 },
  groupPill: (active) => ({
    flex: 1, padding: '4px 8px', borderRadius: 20, fontSize: 10, fontFamily: font,
    letterSpacing: 0.5, textTransform: 'uppercase', cursor: 'pointer',
    border: `1px solid ${active ? ink : cardBorder}`,
    background: active ? ink : 'transparent',
    color: active ? '#F5F0E8' : muted,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  }),
  toggleRow: { display: 'flex', gap: 14, marginBottom: 12 },
  toggleOption: { display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' },
  toggleRadio: (checked) => ({
    width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
    border: `2px solid ${checked ? ink : cardBorder}`,
    background: checked ? ink : 'transparent',
    boxSizing: 'border-box',
  }),
  toggleText: {
    fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
    fontFamily: font, color: muted,
  },

  // Standings
  standingsHeaderRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    marginBottom: 6,
  },
  standingsTitle: { fontSize: 11, fontWeight: 700, color: ink, fontFamily: font },
  colHeaders: { display: 'flex' },
  colH: {
    fontSize: 10, color: muted, fontFamily: font,
    width: 28, textAlign: 'right',
  },
  standingsDivider: { borderBottom: '1px solid #E8E0D0', marginBottom: 5 },
  noData: { fontSize: 12, color: muted, fontStyle: 'italic', fontFamily: font, margin: 0 },

  playerRow: (isViewing, isSelf, clickable) => ({
    display: 'flex', alignItems: 'center', gap: 4, padding: '4px 3px',
    cursor: clickable ? 'pointer' : 'default', borderRadius: 2,
    background: isViewing ? '#FEF9E8' : 'transparent',
    minWidth: 0,
  }),
  previewMsg: {
    fontSize: 11, color: '#8A6A1A', fontStyle: 'italic',
    margin: '0 0 6px', fontFamily: font,
  },
  playerRank: {
    fontSize: 11, fontStyle: 'italic', color: muted,
    width: 16, flexShrink: 0, fontFamily: font,
  },
  playerDot: {
    width: 7, height: 7, borderRadius: '50%',
    background: accent, flexShrink: 0,
  },
  playerName: (isSelf) => ({
    flex: 1, minWidth: 0, fontSize: 13, fontFamily: font,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    color: isSelf ? accent : ink,
  }),
  botBadge: {
    flexShrink: 0, fontSize: 10, lineHeight: 1,
    display: 'inline-block', verticalAlign: 'middle',
  },
  statCell: {
    fontSize: 13, color: ink, fontFamily: font,
    width: 28, textAlign: 'right', flexShrink: 0,
  },

}
